import { useEffect, useState } from "react";
import {
  NotebookPen,
  FileText,
  Boxes,
  FolderKanban,
  CheckSquare,
  Table2,
  Loader2,
} from "lucide-react";
import { api } from "../api";
import { useToast } from "../ui/toast";

interface TokenSum {
  model: string;
  provider: string;
  use_case: string;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  eur_cost: string; // Decimal kommt als string
}
interface StatsResp {
  counts: Record<string, number>;
  by_model: TokenSum[];
  total_eur: string;
  period_from: string | null;
  period_to: string | null;
}

const COUNT_META: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: "notes", label: "Notizen", icon: <NotebookPen className="w-4 h-4" /> },
  { key: "documents", label: "Dokumente", icon: <FileText className="w-4 h-4" /> },
  { key: "chunks", label: "Chunks", icon: <Boxes className="w-4 h-4" /> },
  { key: "projects", label: "Projekte", icon: <FolderKanban className="w-4 h-4" /> },
  { key: "tasks", label: "Aufgaben", icon: <CheckSquare className="w-4 h-4" /> },
  { key: "datasets", label: "Tabellen", icon: <Table2 className="w-4 h-4" /> },
];

function eur(s: string | number) {
  const n = typeof s === "string" ? parseFloat(s) : s;
  if (isNaN(n)) return "0.00 €";
  if (n < 0.01) return `${(n * 100).toFixed(3)} ct`;
  return `${n.toFixed(2)} €`;
}

function num(n: number) {
  return n.toLocaleString("de-DE");
}

type Period = "all" | "month" | "week";

// Der /stats-Endpoint ist backend-seitig (noch) nicht vorhanden ("Tracking ab
// Block 2"). In dem Fall liefert der Server den SPA-Fallback (index.html, 200),
// der als String zurueckkommt — NICHT als StatsResp. Ein blindes data.counts[…]
// bzw. data.by_model.map() liess dann die gesamte App crashen (undefined.map).
// Daher: Antwort auf die erwartete Form pruefen und sonst sauber als "nicht
// verfuegbar" behandeln statt zu rendern.
function isStatsResp(v: unknown): v is StatsResp {
  return (
    !!v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    typeof (v as StatsResp).counts === "object" &&
    (v as StatsResp).counts !== null &&
    Array.isArray((v as StatsResp).by_model)
  );
}

export default function StatsPage() {
  const toast = useToast();
  const [data, setData] = useState<StatsResp | null>(null);
  const [period, setPeriod] = useState<Period>("all");
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setUnavailable(false);
      try {
        const qs = (() => {
          if (period === "all") return "";
          const now = new Date();
          const from = new Date(now);
          if (period === "month") from.setDate(now.getDate() - 30);
          else from.setDate(now.getDate() - 7);
          return `?from_=${encodeURIComponent(from.toISOString())}`;
        })();
        const resp = await api.get<unknown>(`/stats${qs}`);
        if (isStatsResp(resp)) {
          setData(resp);
        } else {
          // Endpoint liefert kein valides Statistik-JSON (z.B. SPA-Fallback).
          setData(null);
          setUnavailable(true);
        }
      } catch (e) {
        setData(null);
        setUnavailable(true);
        toast.err("Statistik konnte nicht geladen werden", (e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-text-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade …
      </div>
    );
  }

  if (unavailable || !data) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Statistik</h1>
          <p className="text-sm text-text-2 mt-1">
            Eigene Daten und KI-Kosten.
          </p>
        </header>
        <div className="bg-surface border border-border rounded-lg p-6 text-sm text-text-2 text-center">
          Statistik ist derzeit nicht verfügbar. Das Nutzungs-Tracking wird mit
          Block 2 aktiviert.
        </div>
      </div>
    );
  }

  const counts = data.counts ?? {};
  const byModel = Array.isArray(data.by_model) ? data.by_model : [];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Statistik</h1>
          <p className="text-sm text-text-2 mt-1">
            Eigene Daten und KI-Kosten. Tracking startet ab Deploy von Block 2.
          </p>
        </div>
        <div className="inline-flex bg-surface-3 rounded-md p-0.5 text-xs">
          {(["all", "month", "week"] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={
                period === p
                  ? "px-3 py-1.5 bg-surface rounded-md shadow-sm font-medium"
                  : "px-3 py-1.5 text-text-2 hover:text-text"
              }
            >
              {p === "all" ? "Alles" : p === "month" ? "30 Tage" : "7 Tage"}
            </button>
          ))}
        </div>
      </header>

      {/* Counts */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-2 mb-3">
          Inhalte
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {COUNT_META.map((m) => (
            <div
              key={m.key}
              className="bg-surface border border-border rounded-lg p-3"
            >
              <div className="flex items-center gap-1.5 text-xs text-text-2">
                {m.icon}
                <span>{m.label}</span>
              </div>
              <div className="text-2xl font-semibold mt-1 tabular-nums">{num(counts[m.key] ?? 0)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* KI-Kosten */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-2">
            KI-Kosten pro Modell × Use Case
          </h2>
          <div className="text-sm">
            <span className="text-text-2">Gesamt: </span>
            <span className="font-semibold">{eur(data.total_eur)}</span>
          </div>
        </div>
        {byModel.length === 0 ? (
          <div className="bg-surface border border-border rounded-lg p-6 text-sm text-text-2 text-center">
            Noch keine KI-Calls in diesem Zeitraum.
          </div>
        ) : (
          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-xs uppercase tracking-wider text-text-2">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Modell</th>
                  <th className="text-left px-3 py-2 font-medium">Use Case</th>
                  <th className="text-right px-3 py-2 font-medium">Aufrufe</th>
                  <th className="text-right px-3 py-2 font-medium">Tokens rein</th>
                  <th className="text-right px-3 py-2 font-medium">Tokens raus</th>
                  <th className="text-right px-3 py-2 font-medium">€</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byModel.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs">{r.model}</span>
                      <span className="ml-2 text-[10px] uppercase text-text-3">
                        {r.provider}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-2">
                      {r.use_case}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(r.calls)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(r.tokens_in)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(r.tokens_out)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {eur(r.eur_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-text-3 mt-2">
          Embedding-Kosten sind aktuell nicht erfasst (vernachlässigbar bei
          text-embedding-3-small, ~$0.02 / 1M Tokens).
        </p>
      </section>
    </div>
  );
}
